<template>
  <div v-if="currentPlan && currentPlan.price" class="order-summary-root">
    <div class="checkout__summary-container" :class="{ 'checkout__summary-container--borderless': borderless }">
      <h4 class="text-h4 mb-2 lapis--text text--base">
        {{ $t('fields.checkout.sidebar.summary.headline') }}
      </h4>

      <!-- START: Radio buttons for standard-plan -->
      <v-radio-group v-if="!isEnterprisePlan" v-model="pricePlan" class="radio-selection-billing-group">
        <v-radio color="navy" class="radio-selection-billing-standard-plan" :value="currentPlan.id">
          <template #label>
            <div>
              <span
                class="radio-selection-billing__title"
                v-html="
                  $t('fields.checkout.sidebar.summary.selected', {
                    selected_plan: planSelectedPlanTitle,
                  })
                "
              />
              <span class="radio-selection-billing__billing">{{ $t(getBillingInfoTranslationKey(currentPlan)) }}</span>
            </div>
          </template>
        </v-radio>
      </v-radio-group>
      <!-- END: Radio buttons for standard-plan -->

      <!-- START: Prorated discounts -->
      <div v-if="showProratedDiscounts" class="order-summary__prorated-discounts">
        <div class="order-summary__plan-price">
          {{ $t(getPlanPriceTranslationKey(currentPlan)) }}
          <span>
            {{ returnPlanPrice }}
          </span>
        </div>

        <div class="prorated-discounts__title">
          <span class="d-flex align-center">
            <span class="mr-1">
              {{ $t('fields.checkout.sidebar.summary.prorated_discounts') }}
            </span>
            <HelpTooltip>
              <template #text>
                {{ $t('fields.checkout.sidebar.summary.prorated_discounts_tooltip') }}
              </template>
            </HelpTooltip>
          </span>
          <span>
            <span>
              {{ returnProrateAmount }}
            </span>
          </span>
        </div>

        <v-divider />
      </div>
      <!-- END: Prorated discounts -->

      <!-- START: Subtotal -->
      <div class="order-summary__subtotal">
        <span>
          {{ $t('fields.checkout.sidebar.summary.subtotal') }}
        </span>
        <span v-if="showSubtotalLoading">
          <v-skeleton-loader width="60" height="12" loading type="text" />
        </span>
        <div v-else data-tast="qr-payment-subtotal-price">
          <span v-if="currentPlan.strikethrough_price" class="mr-1 striketrough_subtotal">
            {{ returnStrikethroughPrice }}
          </span>
          <span>{{ returnSubTotal }}</span>
        </div>
      </div>
      <!-- END: Subtotal -->

      <!-- START: Tax rate -->
      <div class="order-summary__tax">
        <span>
          <span v-if="showTaxSales">
            {{ $t('fields.checkout.sidebar.summary.tax_sales') }}
          </span>
          <span v-else-if="stripeCheckoutService.location.country_iso2">
            {{ $t('fields.checkout.sidebar.summary.tax') }}
          </span>
          <span v-if="showTaxRate">
            (<span data-test="qr-payment-tax-rate">{{ stripeCheckoutService.location.vat_rate }}%</span>)
          </span>
        </span>
        <template v-if="!stripeCheckoutService.customer.address.postal_code">
          {{ $t('fields.checkout.sidebar.summary.address_missing') }}
        </template>
        <span v-else-if="showTaxLoading">
          <v-skeleton-loader width="60" height="12" loading type="text" />
        </span>
        <span
          v-else-if="stripeCheckoutService.location.taxLookupFailed"
          class="error--text"
          data-test="qr-payment-tax-error"
        >
          {{ returnTax }}
        </span>
        <span v-else-if="taxExempt === 'none'" data-test="qr-payment-tax-amount">
          {{ returnTax }}
        </span>
        <span v-else-if="taxExempt === 'reverse'">
          {{ $t('fields.checkout.sidebar.summary.tax_reverse_charged') }}
        </span>
        <span v-else-if="taxExempt === 'exempt'">
          {{ $t('fields.checkout.sidebar.summary.tax_exempt') }}
        </span>
        <span v-else>
          <v-skeleton-loader width="60" height="12" loading type="text" />
        </span>
      </div>
      <!-- END: Tax rate -->

      <template v-if="stripeCheckoutService.customer.address.postal_code">
        <v-divider />

        <!-- START: Total amount -->
        <v-row>
          <v-col class="order-summary__calculated-price">
            <span class="text-uppercase">{{ $t('fields.checkout.sidebar.summary.total') }}</span>
            <span v-if="showTotalPrice" class="order-summary__calculated-price--sum" data-test="qr-payment-total-price">
              {{ totalPrice }}
            </span>
            <span v-else>
              <v-skeleton-loader width="80" height="16" loading type="text" />
            </span>
          </v-col>
        </v-row>
        <!-- END: Total amount -->
      </template>

      <v-row v-if="returnGrossPayment.price < 0">
        <v-col class="order-summary__credit-info">
          <div>
            {{ $t('fields.checkout.sidebar.summary.credit_info') }}
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script lang="ts">
import { planTitleKey } from '@/Services/Subscription/PlanViewService';
import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import { mapState } from 'vuex';
import Currency from '@/Models/PrimitiveModels/Currency/Currency';
import StripeCheckoutService from '@/Services/Subscription/StripeCheckoutService';
import StripeSubscriptionService from '@/Services/Subscription/StripeSubscriptionService';
import axios, { AxiosError } from 'axios';
import AppConfig from '@/AppConfig';
import * as Sentry from '@sentry/vue';
import { Plan, Subscription, SubscriptionState } from '@/classes/stripe';
import { LocationState } from '@/store/location';
import { TranslateResult } from 'vue-i18n';

@Component({
  components: {
    HelpTooltip: () => import('@/components/HelpTooltip.vue'),
  },
  computed: {
    ...mapState<SubscriptionState>('subscription', {
      activeSubscription: (state: SubscriptionState) => state.activeSubscription,
    }),
    ...mapState<LocationState>('location', {
      loadingVatRate: (state: LocationState) => state.showLoading.loadingVatRate,
    }),
  },
})
export default class OrderSummary extends Vue {
  @Prop()
  selectedPlan!: Plan;

  @Prop()
  stripeCheckoutService!: StripeCheckoutService;

  @Prop()
  legalEntity?: string;

  @Prop()
  checkoutType?: string;

  // Renders the summary without the bordered card styling.
  @Prop({ type: Boolean, default: false })
  borderless!: boolean;

  activeSubscription!: Subscription;

  loadingVatRate!: boolean;

  urlPlanId?: string;

  stripeSubscriptionService: StripeSubscriptionService = new StripeSubscriptionService();

  proration_behavior: Currency = new Currency();

  prorateLoading = true;

  isEnterprisePlan = false;

  upgrading = false;

  pricePlan?: string = '';

  // Local clone so we can update plan details without mutating prop
  internalSelectedPlan: Plan = new Plan();

  get currentPlan(): Plan {
    return this.internalSelectedPlan;
  }

  getBillingInfoTranslationKey(plan: Plan): string {
    if (plan.interval === 'year') {
      return 'fields.checkout.sidebar.summary.selected_annual';
    }
    if (plan.interval === 'month' && plan.interval_count == 3) {
      return 'fields.checkout.sidebar.summary.selected_quarter';
    }
    if (plan.interval === 'month' && plan.interval_count == 1) {
      return 'fields.checkout.sidebar.summary.selected_monthly';
    }

    return 'fields.checkout.sidebar.summary.selected_annual';
  }

  getPlanPriceTranslationKey(plan: Plan): string {
    if (plan.interval === 'year') {
      return 'fields.checkout.sidebar.summary.planprice_yearly';
    }
    if (plan.interval === 'month' && plan.interval_count == 3) {
      return 'fields.checkout.sidebar.summary.planprice_quarterly';
    }
    if (plan.interval === 'month' && plan.interval_count == 1) {
      return 'fields.checkout.sidebar.summary.planprice_monthly';
    }

    return 'fields.checkout.sidebar.summary.planprice_yearly';
  }

  // Emits event when payment period changes
  // When user clicks monthly/annual option, update internal plan + parent
  @Watch('pricePlan', { immediate: true })
  async paymentPeriodChanged(pricePlanId: string): Promise<void> {
    if (pricePlanId) {
      const nextPlan = this.stripeSubscriptionService.findPlan(pricePlanId);
      if (nextPlan) {
        this.syncSelectedPlan(nextPlan, true);
        this.$emit('pricePlanChanged', nextPlan);
      }
    }

    if (this.upgrading) {
      await this.getProratedDiscount();
    }
  }

  // Emits event when checkout type changes
  @Watch('checkoutType', { immediate: true })
  async checkoutTypeChanged(checkoutType: string): Promise<void> {
    this.upgrading = checkoutType === 'upgrade';

    if (this.upgrading) {
      await this.getProratedDiscount();
    } else {
      this.prorateLoading = false;
    }
  }

  // Keep internal clone aligned with upstream prop updates
  @Watch('selectedPlan', { immediate: true, deep: true })
  onSelectedPlanPropChanged(plan: Plan): void {
    this.syncSelectedPlan(plan);
  }

  created(): void {
    this.urlPlanId = this.$route.params.paramsId;
    if (!this.pricePlan) {
      this.pricePlan = this.urlPlanId || this.selectedPlan?.id || '';
    }
    this.proration_behavior.useGrouping = true;
  }

  async getProratedDiscount(): Promise<void> {
    if (!this.currentPlan?.id) {
      return;
    }

    this.prorateLoading = true;

    return await axios
      .get(AppConfig.getAPIBaseUrl() + '/subscription/stripe/proration/show/' + this.currentPlan.id)
      .then((response) => {
        this.proration_behavior.price = this.proration_behavior.fromMinorUnits(
          response.data.proration_behavior,
          this.currentPlan.currency
        );
      })
      .catch((error: AxiosError) => {
        Sentry.captureException(error);
      })
      .finally(() => {
        this.prorateLoading = false;
      });
  }

  get taxExempt(): string {
    return this.stripeCheckoutService.location.tax_exempt;
  }

  get returnPlanPrice(): string {
    return this.currentPlan.price.displayPrice({
      price: this.stripeCheckoutService.calcGrossPayment(this.currentPlan, 0, 1).price,
      overrideCurrency: this.currentPlan.currency,
    });
  }

  get returnProrateAmount(): string {
    return this.currentPlan.price.displayPrice({
      overrideCurrency: this.currentPlan.currency,
      price: this.proration_behavior.price,
      useGrouping: true,
    });
  }

  get showProratedDiscounts(): boolean {
    return (
      !!this.proration_behavior.price &&
      this.upgrading &&
      !!this.taxExempt &&
      !this.prorateLoading &&
      !this.loadingVatRate
    );
  }

  get showSubtotalLoading(): boolean {
    return this.taxExempt === '' && this.prorateLoading;
  }

  // The tax amount is derived from the proration, so it is not final until proration resolves.
  get showTaxLoading(): boolean {
    return this.loadingVatRate || this.prorateLoading;
  }

  get showTaxRate(): boolean {
    return (
      !this.loadingVatRate &&
      !this.stripeCheckoutService.location.taxLookupFailed &&
      this.taxExempt === 'none' &&
      !!this.stripeCheckoutService.customer.address.postal_code
    );
  }

  // A failed lookup is terminal: the tax stays unknown, but the total must still settle
  // instead of skeleton-loading forever.
  get showTotalPrice(): boolean {
    return (
      (!!this.taxExempt || this.stripeCheckoutService.location.taxLookupFailed) &&
      !this.prorateLoading &&
      !this.loadingVatRate
    );
  }

  get returnSubTotal(): string {
    const calcGrossPayment = this.stripeCheckoutService.calcGrossPayment(
      this.currentPlan,
      0,
      1,
      this.proration_behavior.price
    );
    return this.currentPlan.price.displayPrice({
      overrideCurrency: this.currentPlan.currency,
      price: calcGrossPayment.price,
    });
  }

  get returnStrikethroughPrice(): string {
    return this.currentPlan.price.displayPrice({
      overrideCurrency: this.currentPlan.currency,
      price: this.currentPlan.strikethrough_price || 0,
    });
  }

  get returnGrossPayment(): Currency {
    return this.stripeCheckoutService.calcGrossPayment(
      this.currentPlan,
      this.stripeCheckoutService.location.vat_rate,
      1,
      this.proration_behavior.price
    );
  }

  get returnVatRateTax(): Currency {
    return this.stripeCheckoutService.calcVatRateTax(
      this.currentPlan,
      this.stripeCheckoutService.location.vat_rate,
      1,
      this.proration_behavior.price
    );
  }

  get returnTax(): string {
    return this.currentPlan.price.displayPrice({
      price: this.returnVatRateTax.price,
      overrideCurrency: this.currentPlan.currency,
    });
  }

  // Whether to label the tax line "Sales tax" (US) vs "Tax"/VAT (everything else).
  // Stripe Tax: any US location that is being taxed is US sales tax. Whether the location is
  // actually taxable comes from the backend's { taxable, rate } response, not a hardcoded list.
  get showTaxSales(): boolean {
    const { country_iso2 } = this.stripeCheckoutService.location;

    return country_iso2 === 'US';
  }

  get totalPrice(): string {
    const intPriceString = Math.round(this.returnGrossPayment.price * 100);
    this.$emit('totalPriceChanged', intPriceString);

    return this.currentPlan.price.displayPrice({
      price: this.returnGrossPayment.price,
      overrideCurrency: this.currentPlan.currency,
    });
  }

  get planSelectedPlanTitle(): TranslateResult {
    return this.$t(planTitleKey(this.currentPlan));
  }

  isPlanVisible(plan: Plan): boolean {
    return plan.currency === this.currentPlan.currency;
  }

  /**
   * Clone the incoming plan so this component can safely adjust it
   * (e.g. when switching billing intervals) without mutating the prop.
   */
  syncSelectedPlan(plan?: Plan, skipPricePlanUpdate = false): void {
    if (!plan) {
      this.internalSelectedPlan = new Plan();
      this.isEnterprisePlan = false;
      if (!skipPricePlanUpdate) {
        this.pricePlan = '';
      }
      return;
    }

    this.internalSelectedPlan = new Plan(plan);
    this.isEnterprisePlan = this.internalSelectedPlan.isEnterprisePlan;

    if (!skipPricePlanUpdate) {
      this.pricePlan = this.internalSelectedPlan.id;
    }
  }
}
</script>

<style lang="scss">
.order-summary-root {
  // Radio button group
  .radio-selection-billing-group {
    padding: 0;
    margin: 0 0 4px;

    .v-messages {
      display: none;
    }

    label {
      font-size: 16px;
      height: 100%;
      font-weight: map-get($font-weights, 'regular');
      color: map-get($lapis, 'lighten-2') !important;
    }
  }
  // Radio buttons styles for standard plan
  .radio-selection-billing-standard-plan {
    border-bottom: 1px solid rgba(map-get($neutral, 'darken-3'), 0.38) !important;

    .v-input--selection-controls__input {
      display: none;
    }
  }
  // Label styles for all plans
  .radio-selection-billing-standard-plan {
    label {
      font-size: 16px;
      height: 100%;
      color: map-get($lapis, 'lighten-2');
      padding-right: 12px;
      cursor: auto !important;

      div {
        display: flex;
        justify-content: space-between;
        width: 100%;
      }
    }
  }

  .striketrough_subtotal {
    color: map-get($lapis, 'lighten-2');
    display: inline-block;
    position: relative;
    &::after {
      border-bottom: 1px solid;
      position: absolute;
      content: '';
      bottom: 50%;
      left: 0px;
      right: 1px;
    }
  }

  // Order summary, which contains all price info - below radio-group
  .order-summary {
    &__subscription-name {
      margin-bottom: 12px;
      font-size: 16px;
      line-height: 24px;
      color: map-get($lapis, 'lighten-2');

      span {
        font-weight: map-get($font-weights, 'regular');
      }
    }

    &__prorated-discounts {
      .prorated-discounts__title {
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: map-get($lapis, 'lighten-2');
        margin-bottom: 12px;
        font-size: 14px;
      }

      .prorated-discounts__price-components {
        .price-components-list {
          padding: 6px 0 12px;
        }

        .price-components-item {
          color: map-get($lapis, 'lighten-2');
          font-size: 12px;
          display: flex;
          justify-content: space-between;
          padding: 4px 0;
        }
      }
    }

    &__subtotal,
    &__tax,
    &__plan-price,
    &__calculated-price {
      display: flex;
      justify-content: space-between;
    }

    &__subtotal,
    &__plan-price {
      font-size: 14px;
      font-weight: map-get($font-weights, 'semi-bold');
      text-transform: uppercase;
      color: map-get($lapis, 'lighten-2');
      margin: 12px 0 4px;
    }

    &__plan-price {
      margin: 12px 0 0;
      text-transform: none;
    }

    &__tax {
      font-size: 14px;
      margin-bottom: 12px;
      color: map-get($lapis, 'lighten-2');
    }

    &__calculated-price {
      font-size: 18px;
      line-height: 24px;
      font-weight: map-get($font-weights, 'bold');
      color: map-get($navy, 'base');
      padding: 8px 12px 0;

      &--sum {
        color: map-get($lapis, 'lighten-2');
      }
    }

    &__credit-info {
      font-size: 12px;
      line-height: 16px;
      font-weight: map-get($font-weights, 'italic');
      color: map-get($lapis, 'lighten-2');
      padding: 12px 12px 0;
    }
  }
}
</style>
